/**
 * useDecisionesFila — almacén único, por id, de lo que decide el técnico (`C0-b`).
 *
 * Primera pieza de `C0` (cada fila tiene su estado de P2 a P4). Hoy solo escribe
 * P3; P2 escribirá en `C0-c` y P4 leerá en `C0-d`. Vive en el asistente, así que
 * sobrevive a salir de P3 y volver; se vacía al cambiar de documento.
 *
 * Qué se decide en P3 (DOP selladas por Luis el 23-oct-2026, `AUDIT_C0B.md` §6):
 * - Ámbito `catalogo`: un elemento del catálogo oficial que falta en el plan
 *   (ausente). La clave es el id de la fuente (`idFuente`), como `ref_externa` en
 *   `MODELO_PTEL_v1.md`. «Añadir al PTEL» → `propuesto`; «Ignorar» → `descartado`.
 * - Ámbito `plan`: una fila del documento (incluido o sobrante). La clave es el id
 *   de la fila. «Ignorar» → `oculto`: solo la esconde en P3, **nunca** la quita de
 *   lo que exporta P4 (DOP-4).
 * - Sin decisión → `pendiente`.
 *
 * De cada decisión se guarda valor, autor y fecha (DOP-2). El autor es el `uid`
 * de la cuenta, nunca el correo (regla del modelo: sin datos personales, DOP-12).
 */

import { useCallback, useReducer } from 'react';
import type { CotejoItem } from '@/components/wizard-v2/step3-coteja/cotejoItem';

export type AmbitoDecision = 'catalogo' | 'plan';
export type ValorDecision = 'pendiente' | 'propuesto' | 'descartado' | 'oculto';

export interface DecisionFila {
  readonly valor: ValorDecision;
  readonly ambito: AmbitoDecision;
  /** `uid` de quien decide; `null` si no hay sesión. Nunca el correo. */
  readonly autor: string | null;
  /** Fecha ISO 8601 de la decisión. */
  readonly fecha: string;
}

/** Mapa id → decisión. Un id sin entrada está `pendiente`. */
export type AlmacenDecisiones = Readonly<Record<string, DecisionFila>>;

export type AccionDecision =
  | {
      readonly tipo: 'decidir';
      readonly id: string;
      readonly valor: ValorDecision;
      readonly ambito: AmbitoDecision;
      readonly autor: string | null;
      readonly fecha: string;
    }
  | { readonly tipo: 'vaciar' }
  /** C0-e2: repone lo guardado en la caché de sesión al pulsar «Recuperar». Sustituye el almacén entero. */
  | { readonly tipo: 'reponer'; readonly guardado: unknown };

/** Valores que admite cada ámbito: `oculto` es solo del plan; `propuesto` y `descartado`, solo del catálogo. */
const VALORES_POR_AMBITO: Record<AmbitoDecision, readonly ValorDecision[]> = {
  catalogo: ['pendiente', 'propuesto', 'descartado'],
  plan: ['pendiente', 'oculto'],
};

export const ALMACEN_VACIO: AlmacenDecisiones = Object.freeze({});

function esAmbito(x: unknown): x is AmbitoDecision {
  return x === 'catalogo' || x === 'plan';
}

/**
 * C0-e2 (DOP-3 a): valida lo que viene de `sessionStorage` antes de reponerlo.
 * Cada entrada debe tener ámbito conocido, un valor que ese ámbito admita, autor
 * texto o `null` y fecha legible. Las inválidas se descartan con `console.error`
 * (no se tapan: HX-FALLBACK-ENMASCARA-BUG); las válidas se conservan.
 */
export function validarAlmacenGuardado(guardado: unknown): AlmacenDecisiones {
  if (guardado === undefined || guardado === null) return ALMACEN_VACIO;
  if (typeof guardado !== 'object' || Array.isArray(guardado)) {
    console.error('[useDecisionesFila] Las decisiones guardadas no son un mapa; no se reponen.', guardado);
    return ALMACEN_VACIO;
  }
  const salida: Record<string, DecisionFila> = {};
  for (const [id, d] of Object.entries(guardado as Record<string, unknown>)) {
    const e = d as Partial<Record<keyof DecisionFila, unknown>> | null;
    const valida = !!e && typeof e === 'object'
      && esAmbito(e.ambito)
      && VALORES_POR_AMBITO[e.ambito].includes(e.valor as ValorDecision)
      && (e.autor === null || typeof e.autor === 'string')
      && typeof e.fecha === 'string' && !Number.isNaN(new Date(e.fecha).getTime());
    if (!valida) {
      console.error(`[useDecisionesFila] Decisión guardada inválida para el id ${id}; se descarta.`, d);
      continue;
    }
    salida[id] = {
      valor: e!.valor as ValorDecision,
      ambito: e!.ambito as AmbitoDecision,
      autor: e!.autor as string | null,
      fecha: e!.fecha as string,
    };
  }
  return Object.keys(salida).length === 0 ? ALMACEN_VACIO : salida;
}

export function reducirDecisiones(
  estado: AlmacenDecisiones,
  accion: AccionDecision,
): AlmacenDecisiones {
  if (accion.tipo === 'vaciar') {
    return Object.keys(estado).length === 0 ? estado : ALMACEN_VACIO;
  }
  if (accion.tipo === 'reponer') {
    const repuesto = validarAlmacenGuardado(accion.guardado);
    return repuesto === ALMACEN_VACIO && Object.keys(estado).length === 0 ? estado : repuesto;
  }
  const { id, valor, ambito, autor, fecha } = accion;
  if (!VALORES_POR_AMBITO[ambito].includes(valor)) {
    // No se tapa: una combinación imposible es un fallo de quien llama.
    console.error(`[useDecisionesFila] «${valor}» no vale para el ámbito «${ambito}» (id ${id}); se ignora.`);
    return estado;
  }
  return { ...estado, [id]: { valor, ambito, autor, fecha } };
}

/** Valor de la decisión sobre un id; `pendiente` si no hay ninguna. */
export function valorDe(almacen: AlmacenDecisiones, id: string): ValorDecision {
  return almacen[id]?.valor ?? 'pendiente';
}

/** Ámbito de un ítem de P3 según su origen: los ausentes vienen del catálogo; el resto, del plan. */
export function ambitoDe(item: Pick<CotejoItem, 'kind'>): AmbitoDecision {
  return item.kind === 'ausente' ? 'catalogo' : 'plan';
}

/**
 * Aplica el almacén a los ítems tal como salen del cotejo: `propuesto` pasa el
 * ausente a incluido; `descartado` y `oculto` lo quitan de la lista (DOP-3 b).
 * El ámbito se toma del ítem original, no del `kind` ya cambiado.
 */
export function aplicarDecisiones(
  items: readonly CotejoItem[],
  almacen: AlmacenDecisiones,
): CotejoItem[] {
  const salida: CotejoItem[] = [];
  for (const item of items) {
    const valor = valorDe(almacen, item.id);
    if (valor === 'descartado' || valor === 'oculto') continue;
    if (valor === 'propuesto' && item.kind === 'ausente') {
      salida.push({ ...item, kind: 'incluido' });
      continue;
    }
    salida.push(item);
  }
  return salida;
}

/** Reloj por defecto, estable entre renders para que `decidir` no cambie de identidad. */
const relojDelSistema = (): Date => new Date();

export interface UseDecisionesFila {
  readonly decisiones: AlmacenDecisiones;
  readonly decidir: (id: string, valor: ValorDecision, ambito: AmbitoDecision) => void;
  readonly vaciar: () => void;
  /** C0-e2: repone lo guardado en la caché (validado entrada a entrada). */
  readonly reponer: (guardado: unknown) => void;
}

/**
 * @param autor `uid` de la cuenta en sesión (o `null`).
 * @param ahora reloj inyectable para pruebas.
 */
export function useDecisionesFila(
  autor: string | null,
  ahora: () => Date = relojDelSistema,
): UseDecisionesFila {
  const [decisiones, despachar] = useReducer(reducirDecisiones, ALMACEN_VACIO);
  const decidir = useCallback(
    (id: string, valor: ValorDecision, ambito: AmbitoDecision) => {
      despachar({ tipo: 'decidir', id, valor, ambito, autor, fecha: ahora().toISOString() });
    },
    [autor, ahora],
  );
  const vaciar = useCallback(() => despachar({ tipo: 'vaciar' }), []);
  const reponer = useCallback((guardado: unknown) => despachar({ tipo: 'reponer', guardado }), []);
  return { decisiones, decidir, vaciar, reponer };
}
